"use client";

import { useEffect, useRef, useState, type JSX } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { stegaClean } from "next-sanity";
import type {
  MuxCSSProperties,
  MuxPlayerRefAttributes,
} from "@mux/mux-player-react";
import { cn } from "@repo/ui/utils";
import { PlayButton } from "@repo/ui/play-button";
import { ImageWrapper } from "../image/image.component";
import type { MuxVideoSource, VideoPlayerConfigData } from "@/lib/types";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => null,
});

export interface MuxVideoPlayerProps {
  source: MuxVideoSource;
  config: VideoPlayerConfigData;
  className?: string;
  /**
   * Render the video as a decorative background — autoplays muted on a
   * loop, fills the container with object-fit: cover, hides both the
   * native controls and the play-button overlay.
   *
   * Forces autoplay/muted/loop on top of the Sanity config because
   * browsers block unmuted autoplay.
   */
  asBackground?: boolean;
  /** Marks the poster image as the LCP candidate (above-the-fold heroes). */
  priority?: boolean;
}

export function MuxVideoPlayer({
  source,
  config,
  className,
  asBackground = false,
  priority = false,
}: MuxVideoPlayerProps): JSX.Element | null {
  const playbackId = stegaClean(source?.videoFile?.asset?.playbackId);
  const thumbnail = config?.thumbnail;
  const thumbTime = source?.videoFile?.asset?.thumbTime;
  const playerRef = useRef<MuxPlayerRefAttributes | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Defer loading the ~1MB Mux + hls.js player chunk until the video is near
  // the viewport — offscreen videos no longer block first load. Above-the-fold
  // videos intersect on mount and load immediately, so playback is unchanged.
  const [isNearViewport, setIsNearViewport] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      queueMicrotask(() => setIsNearViewport(true));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const autoPlay = asBackground || (config?.autoPlay ?? false);
  const muted = autoPlay || (config?.isMuted ?? false);
  const loop = asBackground || (config?.isLooping ?? false);
  const showControls = asBackground ? false : (config?.showControls ?? true);
  const showPlayOverlay = !asBackground && !autoPlay;

  // Poster stays up until the video genuinely renders frames — covers SSR
  // first paint, the deferred player chunk download, and blocked autoplay.
  const [isPlaying, setIsPlaying] = useState(false);

  if (!playbackId) {
    return null;
  }

  // Mux defaults thumbnails to the video midpoint; pin to the opening shot
  // unless an explicit thumbTime is set on the asset.
  const fallbackPosterUrl = `https://image.mux.com/${playbackId}/thumbnail.webp?time=${thumbTime ?? 0}`;

  const style: MuxCSSProperties = {
    "--media-accent-color": "var(--color-sunshine-yellow-500)",
    ...(showControls ? {} : { "--controls": "none" }),
    ...(asBackground
      ? {
          "--media-object-fit": "cover",
          "--media-object-position": "center",
        }
      : {}),
  };

  const handlePlay = (): void => {
    setIsPlaying(true);
    playerRef.current?.play().catch(() => {
      // Surfacing autoplay errors to the user is out of scope; ignore.
    });
  };

  return (
    <div ref={containerRef} className={cn("relative h-full w-full", className)}>
      {isNearViewport ? (
        <MuxPlayer
          ref={playerRef}
          className="h-full w-full"
          playbackId={playbackId}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={asBackground || undefined}
          style={style}
          onPlaying={() => setIsPlaying(true)}
        />
      ) : null}
      {!isPlaying ? (
        <div className="absolute inset-0 z-10">
          {thumbnail ? (
            <ImageWrapper
              visual={thumbnail}
              className="h-full w-full"
              priority={priority}
            />
          ) : (
            <Image
              src={fallbackPosterUrl}
              alt=""
              fill
              sizes="100vw"
              priority={priority}
              className="object-cover"
            />
          )}
          {showPlayOverlay ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <PlayButton
                className="pointer-events-auto"
                onClick={handlePlay}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

MuxVideoPlayer.displayName = "MuxVideoPlayer";
